import React from 'react';
import {
  Card,
  Typography,
  Box,
  Chip,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Project } from '../types';
import { formatDate, calculateDaysRemaining } from '../utils/helpers';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(3.5),
  marginBottom: theme.spacing(3),
  backgroundColor: '#ffffff',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.06)',
  border: '1px solid #e2e8f0',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  },
}));

const ProjectHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(3),
  gap: theme.spacing(2),
  flexWrap: 'wrap',
}));

const ProjectTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.75rem',
  fontWeight: 700,
  color: '#1a202c',
  letterSpacing: '-0.02em',
  lineHeight: 1.3,
}));

const StatusChip = styled(Chip)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.8125rem',
  height: 28,
  borderRadius: theme.spacing(1),
}));

const DescriptionBox = styled(Box)(({ theme }) => ({
  fontSize: '0.9375rem',
  lineHeight: 1.6,
  color: '#475569',
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2, 2.5),
  backgroundColor: '#f8fafc',
  borderLeft: '3px solid #3b82f6',
  borderRadius: theme.spacing(0.5),
}));

const InfoGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

const InfoCard = styled(Box)(({ theme }) => ({
  background: '#f8fafc',
  padding: theme.spacing(2.5),
  borderRadius: theme.spacing(1),
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: '#f1f5f9',
    transform: 'translateY(-1px)',
  },
}));

const InfoIcon = styled(Box)(({ theme }) => ({
  width: 36,
  height: 36,
  borderRadius: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#ffffff',
  border: '1px solid #e2e8f0',
  '& .MuiSvgIcon-root': {
    fontSize: '1.125rem',
    color: '#64748b',
  },
}));

const InfoContent = styled(Box)({
  flex: 1,
});

const InfoLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 600,
  color: '#94a3b8',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: theme.spacing(0.5),
}));

const InfoValue = styled(Typography)(({ theme }) => ({
  fontSize: '0.9375rem',
  fontWeight: 600,
  color: '#1e293b',
  lineHeight: 1.4,
}));

const ProgressSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#f8fafc',
  padding: theme.spacing(2.5),
  borderRadius: theme.spacing(1),
  marginTop: theme.spacing(3),
}));

const ProgressHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  marginBottom: theme.spacing(1.5),
}));

const ProgressLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 600,
  color: '#475569',
}));

const ProgressValue = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: 700,
  color: '#1e293b',
}));

const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  backgroundColor: '#e2e8f0',
  '& .MuiLinearProgress-bar': {
    borderRadius: 5,
    background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
  },
}));

const IssueAlert = styled(Box)(({ theme }) => ({
  backgroundColor: '#fef2f2',
  border: '1px solid #fecaca',
  borderRadius: theme.spacing(1),
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}));

interface ProjectCardProps {
  project: Project;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project }) => {
  const getStatusConfig = () => {
    const statusMap = {
      active: { 
        label: '進行中', 
        color: '#10b981',
        bg: '#d1fae5',
        borderColor: '#6ee7b7',
      },
      pending: { 
        label: '待開始', 
        color: '#f59e0b',
        bg: '#fef3c7',
        borderColor: '#fcd34d',
      },
      completed: { 
        label: '已完成', 
        color: '#3b82f6',
        bg: '#dbeafe',
        borderColor: '#93c5fd',
      },
    };
    
    return statusMap[project.status];
  };

  const getProgressStatus = () => {
    const diff = project.progress - project.expectedProgress;
    if (diff < -10) return { color: '#ef4444', label: '落後' };
    if (diff > 10) return { color: '#10b981', label: '超前' };
    return { color: '#3b82f6', label: '正常' };
  };

  const statusConfig = getStatusConfig();
  const progressStatus = getProgressStatus();
  const daysRemaining = calculateDaysRemaining(project.expectedDate);

  return (
    <StyledCard elevation={0}>
      <ProjectHeader>
        <Box sx={{ flex: 1 }}>
          <ProjectTitle>
            {project.name}
          </ProjectTitle>
        </Box>
        <StatusChip
          label={statusConfig.label}
          sx={{
            backgroundColor: statusConfig.bg,
            color: statusConfig.color,
            border: `1px solid ${statusConfig.borderColor}`,
          }}
        />
      </ProjectHeader>

      <DescriptionBox>
        {project.description}
      </DescriptionBox>

      <InfoGrid>
        <InfoCard>
          <InfoIcon>
            <CalendarTodayIcon />
          </InfoIcon>
          <InfoContent>
            <InfoLabel>專案期程</InfoLabel>
            <InfoValue>
              {formatDate(project.startDate)} - {formatDate(project.expectedDate)}
            </InfoValue>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              剩餘 {daysRemaining} 天
            </Typography>
          </InfoContent>
        </InfoCard>
        
        <InfoCard>
          <InfoIcon>
            <TrendingUpIcon />
          </InfoIcon>
          <InfoContent>
            <InfoLabel>進度狀態</InfoLabel>
            <Tooltip title={`預期進度 ${project.expectedProgress}% / 實際進度 ${project.progress}%`}>
              <InfoValue sx={{ color: progressStatus.color, cursor: 'help' }}>
                {progressStatus.label} ({project.progress - project.expectedProgress > 0 ? '+' : ''}{project.progress - project.expectedProgress}%)
              </InfoValue>
            </Tooltip>
          </InfoContent>
        </InfoCard>
        
        <InfoCard>
          <InfoIcon>
            <PersonIcon />
          </InfoIcon>
          <InfoContent>
            <InfoLabel>專案經理</InfoLabel>
            <InfoValue>{project.pmName}</InfoValue>
          </InfoContent>
        </InfoCard>
      </InfoGrid>

      {project.issueCount > 0 && (
        <IssueAlert>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: '#ef4444',
              flexShrink: 0,
            }}
          />
          <Typography variant="body2" sx={{ color: '#991b1b', fontWeight: 500 }}>
            有 {project.issueCount} 個任務驗收失敗，需要處理
          </Typography>
        </IssueAlert>
      )}

      <ProgressSection>
        <ProgressHeader>
          <ProgressLabel>整體完成進度</ProgressLabel>
          <ProgressValue>{project.progress}%</ProgressValue>
        </ProgressHeader>
        <StyledLinearProgress 
          variant="determinate" 
          value={project.progress} 
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            預期進度: {project.expectedProgress}%
          </Typography>
          <Typography 
            variant="caption" 
            sx={{ 
              color: progressStatus.color,
              fontWeight: 600,
            }}
          >
            {progressStatus.label}進度 {Math.abs(project.progress - project.expectedProgress)}%
          </Typography>
        </Box>
      </ProgressSection>
    </StyledCard>
  );
};

export default ProjectCard; 